import { ClickAwayListener, Grid, Switch, Tooltip } from "@material-ui/core";
import { merge } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { Button } from "../../../components/button";
import { Grid as CssGrid } from "../../../components/layout";
import { Loader } from "../../../components/loader";
import { LoyaltyLevel } from "../../../components/loyalty/utils";
import { Permission } from "../../../components/permission";
import { Widget } from "../../../components/widget";
import { BillingDTO } from "../../../store/billing/models/dto";
import {
  additionalFeatures,
  BillableApi,
} from "../../../store/billing/models/internal";
import { Billing } from "../../../store/billing/reducer";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { UsersDTO } from "../../../store/users/models/dto";
import { Users } from "../../../store/users/reducer";
import { colours } from "../../../styles/colours";
import { device } from "../../../styles/media";
import { mapObject, toDecimal } from "../../../utils/utils";
import { cardMap } from "./card";
import { DefaultCard } from "./card/default";
import { LoyaltySlider } from "./loyalty-slider";

const centerText = css`
  text-align: center;
`;

const Balance = styled.h2`
  font-size: 2em;
  font-weight: 300;
  font-family: "Helvetica Neue", sans-serif;
  margin-bottom: 0.75em;
  ${centerText};
`;

const LevelTitle = styled.h2`
  margin-bottom: 0em;
  font-size: 1.3em;
`;

const PendingBalance = styled.span`
  font-size: 0.8em;
  margin-left: 0.3em;
  color: ${colours.darkGrey};
  display: block;

  @media ${device.md} {
    display: inline;
  }
`;

const PendingButton = styled(Button)`
  width: 2em;
  margin: 0 0.5em;
  padding: 0;
  min-width: 0px;
  font-size: 0.4em;
`;

const StyledWidget = styled(Widget)`
  padding: 4em;
`;

const Free = styled.div`
  color: ${colours.green};
  font-size: 1.2em;
  font-weight: 700;
  ${centerText};
`;

const BelieverTitle = styled.h5`
  color: ${colours.gold};
  font-size: 1.4em;
  margin-bottom: 0.2em;
  ${centerText};
`;

const StyledGrid = styled(CssGrid)`
  grid-template-columns: repeat(1, 1fr);

  @media ${device.mobileL} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${device.laptop} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${device.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export function BillingSummaryPage() {
  const [loyaltyLevel, setLoyaltyLevel] = useState<
    typeof LoyaltyLevel.T | undefined
  >();

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const { t } = useTranslation("common");

  const [believer, setBeliever] = useState<boolean>(false);
  const pricing = useFetchable(Billing.pricing);
  const balance = useFetchable(Billing.balance);
  const vaultsBilling = useFetchable(Billing.vaultsBilling);
  const chainBilling = useFetchable(Billing.chainBilling);
  const verificationBilling = useFetchable(
    Billing.verificationBilling,
    undefined
  );
  const currentUser = useFetchable(Users.current);

  // Set state level and believer when we obtain fetchable
  useEffect(() => {
    if (isFetchableSuccess(currentUser)) {
      setLoyaltyLevel(currentUser.data.level);
      setBeliever(currentUser.data.believer);
    }
  }, [currentUser]);

  return (
    <Loader<
      | BillingDTO.Responses.GetPricing.T
      | UsersDTO.Responses.GetSingle.T
      | BillingDTO.Responses.GetBalance.T
      | BillingDTO.Responses.GetBillingInfo.T
    >
      data={[
        pricing,
        currentUser,
        balance,
        chainBilling,
        verificationBilling,
        vaultsBilling,
      ]}
    >
      {(loaded) => {
        const loadedPricing = loaded[0] as BillingDTO.Responses.GetPricing.T;
        const loadedCurrentUser = loaded[1] as UsersDTO.Responses.GetSingle.T;
        const loadedBalance = loaded[2] as BillingDTO.Responses.GetBalance.T;
        const loadedChainBilling = loaded[3] as BillingDTO.Responses.GetBillingInfo.T;
        const loadedVerificationBilling = loaded[4] as BillingDTO.Responses.GetBillingInfo.T;
        const loadedVaultsBilling = loaded[5] as BillingDTO.Responses.GetBillingInfo.T;

        const billingMap: Partial<
          Record<
            typeof BillableApi.T,
            {
              originalPrice: number;
              discount: number;
              freeCredits?: number;
            }
          >
        > = loadedPricing.featureDetails.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.featureName]: {
              originalPrice: curr.originalPrice,
              discount:
                1 -
                ((loadedPricing.levelDiscounts.find(
                  (x) => x.userLevel === loyaltyLevel
                )?.discountRate || 0) +
                  ((believer &&
                    loadedPricing.levelDiscounts.find(
                      (x) => x.userLevel === "BELIEVER"
                    )?.discountRate) ||
                    0)),
            },
          }),
          {}
        );

        [
          loadedChainBilling,
          loadedVerificationBilling,
          loadedVaultsBilling,
        ].forEach((service) =>
          service.apiSpecificInfo.forEach((x) =>
            merge(billingMap[x.apiName], { freeCredits: x.remainingApiCalls })
          )
        );

        const remainingFreeCalls = loadedChainBilling.remainingPlatformCalls;

        return (
          <StyledWidget>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Balance>
                  {t("balance", { context: "title" })}:{" "}
                  {toDecimal(loadedBalance.credits)}{" "}
                  {t("credit", { context: 2 })}
                  {loadedBalance.pendingCredits > 0 ? (
                    <PendingBalance>
                      {toDecimal(
                        loadedBalance.pendingCredits + loadedBalance.credits
                      )}
                      <ClickAwayListener
                        onClickAway={() => setTooltipOpen(false)}
                      >
                        <Tooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          onClose={() => setTooltipOpen(false)}
                          open={tooltipOpen}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          title={t("you-have-credits-pending-approval", {
                            amount: toDecimal(loadedBalance.pendingCredits),
                            ns: "billing",
                          })}
                        >
                          <PendingButton
                            size="small"
                            variant="contained"
                            colour="secondary"
                            onClick={() => setTooltipOpen(true)}
                          >
                            ?
                          </PendingButton>
                        </Tooltip>
                      </ClickAwayListener>
                    </PendingBalance>
                  ) : null}
                </Balance>
                {remainingFreeCalls > 0 ? (
                  <Free>
                    {t("free-calls")}: {remainingFreeCalls}
                  </Free>
                ) : null}
                {loadedCurrentUser.believer ? (
                  <>
                    <BelieverTitle>
                      {t("youre-a-believer")}{" "}
                      <Switch
                        checked={believer}
                        onChange={() => setBeliever(!believer)}
                        color="primary"
                        name="believer"
                      />
                    </BelieverTitle>
                  </>
                ) : null}
              </Grid>
              <Permission roles={["ROLE_CRYPTO"]}>
                <Grid item xs={12}>
                  <LevelTitle>{t("your-current-level")}: </LevelTitle>
                  <LoyaltySlider
                    level={loyaltyLevel}
                    setLevel={setLoyaltyLevel}
                  />
                </Grid>
              </Permission>
              <Grid item xs={12}>
                <StyledGrid>
                  {mapObject(
                    billingMap,
                    ({ originalPrice, discount, freeCredits }, key) => {
                      if (
                        originalPrice === 0.0 ||
                        additionalFeatures.includes(key)
                      )
                        return null;
                      const Comp = cardMap[key] || DefaultCard;

                      return (
                        <Comp
                          credits={loadedBalance.credits}
                          originalPrice={originalPrice}
                          discount={discount}
                          freeCredits={freeCredits}
                          key={key}
                          apiName={BillableApi.enum[key]}
                        />
                      );
                    }
                  )}
                </StyledGrid>
              </Grid>
            </Grid>
          </StyledWidget>
        );
      }}
    </Loader>
  );
}
